import { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackImmersiveTabClick, trackPanelStateChange } from '../utils/analytics'

/**
 * useImmersiveTabs - Custom hook for managing immersive tab state and behavior
 * 
 * Centralizes common tab handling logic used across ImmersiveHome and ImmersiveEntityDetail
 * 
 * @param {Object} options
 * @param {string} options.defaultTab - Default active tab (default: 'search')
 * @param {string} options.defaultPanelState - Default panel state (default: 'expanded')
 * @param {string} options.pageName - Page name for analytics tracking
 * @param {string} options.entityTabState - Panel state for entity/search tab (default: 'semi')
 * @param {Array} options.panelCycle - Panel state cycle order
 * @param {boolean} options.isAuthenticated - User authentication status
 * 
 * @returns {Object} Tab state and handlers
 */
export function useImmersiveTabs({
  defaultTab = 'search',
  defaultPanelState = 'expanded',
  pageName = 'page',
  entityTabState = 'semi',
  panelCycle = ['expanded', 'minimized'],
  isAuthenticated = false
} = {}) {
  const location = useLocation()
  
  const [activeTab, setActiveTab] = useState(location.state?.tab || defaultTab)
  const [panelState, setPanelState] = useState(defaultPanelState)
  
  // Track recent programmatic changes to prevent accidental cycling
  const programmaticChangeRef = useRef(null)

  // Deep linking: Detect tab, announcement, or FAQ parameter on mount and switch tabs
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const tabParam = params.get('tab')
    const announcementSlug = params.get('announcement')
    const faqSlug = params.get('faq')

    // Priority: specific deep links (announcement, faq) take precedence over generic tab parameter
    if (announcementSlug) {
      // Switch to announcements tab with expanded panel
      setActiveTab('announcements')
      setPanelState('expanded')
    } else if (faqSlug) {
      // Switch to FAQ tab with expanded panel
      setActiveTab('faq')
      setPanelState('expanded')
    } else if (tabParam) {
      // Generic tab parameter for direct navigation
      // Valid tabs: search, announcements, faq, auth, contact, live, vendor
      const validTabs = ['search', 'announcements', 'faq', 'auth', 'contact', 'live', 'vendor']
      if (validTabs.includes(tabParam)) {
        setActiveTab(tabParam)
        setPanelState('expanded')
      }
    }
  // End deep-link effect
  }, [])

  /**
   * Handle tab click with state cycling logic
   */
  const handleTabClick = useCallback((tabId, newPanelState = null) => {
    // Handle minimize signal from popup handler
    if (tabId === '_minimize_') {
      const prevState = panelState
      setPanelState('minimized')
      trackPanelStateChange(prevState, 'minimized', activeTab, 'programmatic', pageName)
      return
    }
    
    // If explicit panel state provided AND we're changing tabs, change both
    if (newPanelState !== null && tabId !== activeTab) {
      const prevState = panelState
      setActiveTab(tabId)
      setPanelState(newPanelState)
      trackImmersiveTabClick(tabId, pageName, panelState)
      if (prevState !== newPanelState) {
        trackPanelStateChange(prevState, newPanelState, tabId, 'programmatic', pageName)
      }
      // Mark as programmatic change to prevent cycling for 500ms
      programmaticChangeRef.current = Date.now()
      return
    }
    
    // If explicit panel state provided for SAME tab, just change panel state
    if (newPanelState !== null) {
      const prevState = panelState
      setPanelState(newPanelState)
      if (prevState !== newPanelState) {
        trackPanelStateChange(prevState, newPanelState, activeTab, 'programmatic', pageName)
      }
      // Mark as programmatic change to prevent cycling for 500ms
      programmaticChangeRef.current = Date.now()
      return
    }
    
    // Track tab click
    trackImmersiveTabClick(tabId, pageName, panelState)
    
    // If clicking the same tab that's already active, cycle through states
    if (tabId === activeTab) {
      // Don't cycle if we just had a programmatic change (within 500ms)
      const timeSinceProgrammatic = programmaticChangeRef.current 
        ? Date.now() - programmaticChangeRef.current 
        : Infinity
      
      if (timeSinceProgrammatic < 500) {
        // Too soon after programmatic change, ignore this click
        return
      }
      
      // Cycle through the configured panel states
      const currentIndex = panelCycle.indexOf(panelState)
      const nextIndex = (currentIndex + 1) % panelCycle.length
      const nextState = panelCycle[nextIndex]
      setPanelState(nextState)
      trackPanelStateChange(panelState, nextState, activeTab, 'tab_click', pageName)
    } else {
      // Switching to a different tab
      setActiveTab(tabId)
      // Use entityTabState for 'search' tab, expanded for others
      const nextState = tabId === 'search' ? entityTabState : 'expanded'
      if (panelState !== nextState) {
        trackPanelStateChange(panelState, nextState, tabId, 'tab_click', pageName)
      }
      setPanelState(nextState)
    }
  }, [activeTab, panelState, pageName, entityTabState, panelCycle])

  /**
   * Programmatically change tab
   */
  const changeTab = useCallback((tabId) => {
    setActiveTab(tabId)
    setPanelState('expanded')
  }, [])

  /**
   * Toggle panel between expanded and minimized
   */
  const togglePanel = useCallback(() => {
    setPanelState(prev => prev === 'expanded' ? 'minimized' : 'expanded')
  }, [])

  /**
   * Minimize panel
   */
  const minimizePanel = useCallback(() => {
    setPanelState('minimized')
  }, [])

  /**
   * Expand panel
   */
  const expandPanel = useCallback(() => {
    setPanelState('expanded')
  }, [])

  return {
    activeTab,
    panelState,
    handleTabClick,
    changeTab,
    togglePanel,
    minimizePanel,
    expandPanel,
    setActiveTab,
    setPanelState
  }
}
